import { useEffect, useMemo, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Keyless OpenStreetMap-based map. Every marker is a REAL listing (spot_id,
// lat/lng from the backend). Hover/select sync with the listing list.
const markerHtml = (active, hovered) =>
  `<div style="width:${active || hovered ? 18 : 14}px;height:${active || hovered ? 18 : 14}px;border-radius:999px;background:${active ? "#E50914" : hovered ? "#FF3B45" : "#3F3F46"};border:2px solid ${active || hovered ? "#fff" : "#A1A1AA"};box-shadow:0 2px 10px rgba(0,0,0,.5);transition:all .18s ease"></div>`;

export default function MapView({ spots, selectedId, hoveredId, onSelect, className = "" }) {
  const mapRef = useRef(null);
  const elRef = useRef(null);
  const markersRef = useRef({});

  const center = useMemo(() => {
    if (!spots?.length) return [43.6532, -79.3832];
    const lat = spots.reduce((a, s) => a + s.latitude, 0) / spots.length;
    const lng = spots.reduce((a, s) => a + s.longitude, 0) / spots.length;
    return [lat, lng];
  }, [spots]);

  useEffect(() => {
    if (!elRef.current || mapRef.current) return;
    const map = L.map(elRef.current, { zoomControl: true, attributionControl: true, scrollWheelZoom: true });
    L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}", {
      attribution: "Tiles &copy; Esri &mdash; Esri, DeLorme, NAVTEQ &middot; &copy; OpenStreetMap contributors",
      maxZoom: 16,
    }).addTo(map);
    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; markersRef.current = {}; };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    Object.values(markersRef.current).forEach((m) => m.remove());
    markersRef.current = {};
    (spots || []).forEach((s) => {
      if (s.latitude == null || s.longitude == null) return;
      const m = L.marker([s.latitude, s.longitude], {
        icon: L.divIcon({ className: "", html: markerHtml(selectedId === s.spot_id, hoveredId === s.spot_id), iconSize: [18, 18], iconAnchor: [9, 9] }),
        title: s.title,
      });
      m.on("click", () => onSelect?.(s.spot_id));
      m.bindTooltip(s.title, { direction: "top", offset: [0, -8], className: "mrp-tooltip" });
      m.addTo(map);
      markersRef.current[s.spot_id] = m;
    });
    if (spots?.length) {
      const bounds = L.latLngBounds(spots.filter((s) => s.latitude != null).map((s) => [s.latitude, s.longitude]));
      if (bounds.isValid()) map.fitBounds(bounds.pad(0.18));
    } else {
      map.setView(center, 12);
    }
  }, [spots, selectedId, hoveredId]); // eslint-disable-line

  return <div ref={elRef} className={`w-full h-full min-h-[320px] rounded-2xl border border-[#27272A] overflow-hidden ${className}`} data-testid="parking-map" role="application" aria-label="Parking map" />;
}
